import React, { useState, useEffect, useCallback } from 'react';
import { LanguageContext, SUPPORTED_LANGUAGES } from './LanguageContext';
import { isPublicSeoPath, resolveInitialLanguage } from '../utils/languageRouting';

const translationsCache = {};

async function loadTranslations(lang) {
  if (translationsCache[lang]) return translationsCache[lang];

  try {
    const module = lang === 'cs'
      ? await import('../locales/cs.js')
      : await import('../locales/en.js');
    translationsCache[lang] = module.default;
    return module.default;
  } catch (e) {
    console.error(`Failed to load translations for ${lang}:`, e);
    return {};
  }
}

const DEFAULT_LANGUAGE = 'cs';
const CZECH_ONLY_LEGAL_PATHS = new Set(['/terms', '/privacy', '/dpa']);

function isCzechOnlyLegalLocation() {
  return CZECH_ONLY_LEGAL_PATHS.has(window.location.pathname);
}

function updateDocumentLanguageMetadata(language, url) {
  document.documentElement.lang = language;
  const canonicalUrl = new URL(url.toString());
  if (isCzechOnlyLegalLocation()) canonicalUrl.searchParams.delete('lang');
  const absoluteUrl = canonicalUrl.toString();
  document.querySelector('link[rel="canonical"]')?.setAttribute('href', absoluteUrl);
  document.querySelector('meta[property="og:url"]')?.setAttribute('content', absoluteUrl);
  document.querySelector('meta[name="twitter:url"]')?.setAttribute('content', absoluteUrl);
}

function isPublicSeoLocation() {
  return isPublicSeoPath(window.location.pathname);
}

function getUrlLanguage() {
  if (!isPublicSeoLocation()) return null;
  if (isCzechOnlyLegalLocation()) return DEFAULT_LANGUAGE;
  return new URLSearchParams(window.location.search).get('lang') === 'en' ? 'en' : DEFAULT_LANGUAGE;
}

function getInitialLanguage() {
  if (isCzechOnlyLegalLocation()) return DEFAULT_LANGUAGE;
  return resolveInitialLanguage({
    pathname: window.location.pathname,
    search: window.location.search,
    storedLanguage: localStorage.getItem('controlvitals-language'),
    browserLanguage: navigator.language,
  });
}

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(getInitialLanguage);
  const [translations, setTranslations] = useState(() => translationsCache[getInitialLanguage()] || {});
  const [isLoaded, setIsLoaded] = useState(() => !!translationsCache[getInitialLanguage()]);

  useEffect(() => {
    document.documentElement.lang = language;
    loadTranslations(language).then(t => {
      setTranslations(t);
      setIsLoaded(true);
    });
  }, [language]);

  useEffect(() => {
    const otherLang = language === 'cs' ? 'en' : 'cs';
    const timer = setTimeout(() => {
      loadTranslations(otherLang);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const syncLanguageFromUrl = () => {
      const urlLanguage = getUrlLanguage();
      if (urlLanguage) {
        updateDocumentLanguageMetadata(urlLanguage, new URL(window.location.href));
        setLanguageState(urlLanguage);
      }
    };
    window.addEventListener('popstate', syncLanguageFromUrl);
    return () => window.removeEventListener('popstate', syncLanguageFromUrl);
  }, []);

  const setLanguage = useCallback(async (newLang, syncToServer = true) => {
    if (isCzechOnlyLegalLocation()) newLang = DEFAULT_LANGUAGE;
    if (SUPPORTED_LANGUAGES[newLang]) {
      localStorage.setItem('controlvitals-language', newLang);

      if (isPublicSeoLocation()) {
        const url = new URL(window.location.href);
        if (newLang === 'en') {
          url.searchParams.set('lang', 'en');
        } else {
          url.searchParams.delete('lang');
        }
        window.history.pushState({}, '', `${url.pathname}${url.search}${url.hash}`);
        updateDocumentLanguageMetadata(newLang, url);
      }

      const newTranslations = await loadTranslations(newLang);
      setTranslations(newTranslations);
      setLanguageState(newLang);

      if (syncToServer) {
        try {
          const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
          await fetch('/api/user/language', {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': csrfToken || ''
            },
            credentials: 'include',
            body: JSON.stringify({ language: newLang })
          });
        } catch (error) {
        }
      }
    }
  }, []);

  const t = useCallback((key, paramsOrFallback = null) => {
    const keys = key.split('.');
    let value = translations;

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        if (typeof paramsOrFallback === 'string') {
          return paramsOrFallback;
        }
        return key;
      }
    }

    if (value === undefined || value === null) {
      if (typeof paramsOrFallback === 'string') {
        return paramsOrFallback;
      }
      return key;
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
      console.warn(`Translation key "${key}" returns an object, not a string`);
      if (typeof paramsOrFallback === 'string') {
        return paramsOrFallback;
      }
      return key;
    }

    if (typeof value === 'string' && paramsOrFallback && typeof paramsOrFallback === 'object') {
      return value.replace(/\{\{?(\w+)\}?\}/g, (match, paramName) => {
        return paramsOrFallback[paramName] !== undefined ? paramsOrFallback[paramName] : match;
      });
    }

    return value;
  }, [translations]);

  const syncFromUser = useCallback((userLanguage) => {
    if (userLanguage && SUPPORTED_LANGUAGES[userLanguage]) {
      if (isPublicSeoLocation()) return;
      const storedLang = localStorage.getItem('controlvitals-language');
      if (!storedLang) {
        localStorage.setItem('controlvitals-language', userLanguage);
        setLanguageState(userLanguage);
      }
    }
  }, []);

  const value = {
    language,
    setLanguage,
    syncFromUser,
    languages: SUPPORTED_LANGUAGES,
    t,
    isEnglish: language === 'en',
    isCzech: language === 'cs'
  };

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-950">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}
